import { ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { PageHeader, PageHeaderProps } from 'components/PageHeader';
import { getFieldsHiddenByCriteria } from 'lib/fieldCriteria';
import { getFieldGroupInfo, hasValue } from 'lib/fields';
import * as React from 'react';
import { Field } from 'screens/AssetScreen/Field/Field';
import { FieldGroup } from 'store/organisations/types';
import { PinNew } from 'store/plan/types';
import { AppContext, FieldResult } from 'types/types';

type Props = {
  context: AppContext;
  group: Omit<FieldGroup, 'fields'> & {
    calculatedInfo: ReturnType<typeof getFieldGroupInfo>;
  };
  fields: FieldGroup['fields'];
  editableFieldIds?: string[];
  requiredFieldIds?: string[];
  hiddenFieldIds?: string[];
  results: Record<string, FieldResult>;
  resultsOld?: Record<string, FieldResult>;
  resultsNew?: Record<string, FieldResult>;
  /**
   * Use this to re-render a field after an external value change
   */
  fieldKeys: Record<string, string>;
  onEsc?: () => void;
  onChange: (
    fieldId: string,
    value: string | string[] | null,
    pin?: PinNew | '' | null,
  ) => void;
  onRevert?: (fieldId: string) => void;
  onPressSaveButton?: () => void;
  onPressBackButton?: () => void;
  onPressCancelButton?: () => void;
  headerMenuRight?: PageHeaderProps['menuRight'];
  subheading?: string;
  subheadingIcon?: ICONS;
  onPressSubheading?: () => void;
  saveButtonText?: string;
  showInvalid: boolean;
};

export const GlobalFieldInstanceScreen: React.FC<Props> = ({
  context,
  group,
  fields,
  fieldKeys,
  editableFieldIds,
  requiredFieldIds,
  hiddenFieldIds,
  onEsc,
  onChange,
  onRevert,
  results,
  resultsOld,
  resultsNew,
  headerMenuRight,
  onPressSaveButton,
  onPressBackButton,
  onPressCancelButton,
  onPressSubheading,
  subheading,
  subheadingIcon,
  saveButtonText,
  showInvalid,
}) => {
  const fieldsHaveCriteria = React.useMemo(
    () => group.fieldIds.some((fieldId) => !!fields[fieldId]?.criteria.length),
    [fields, group.fieldIds],
  );

  const fieldIsHiddenByCriteria = fieldsHaveCriteria
    ? getFieldsHiddenByCriteria({
        fieldIds: group.fieldIds,
        fields,
        results,
      })
    : {};

  const fieldIdsForLayout: string[] = [];
  Object.values(group.fieldIds).forEach((fieldId) => {
    const field = fields[fieldId]!;
    if (!hiddenFieldIds?.includes(field.id)) {
      if (!fieldIsHiddenByCriteria[fieldId] || hasValue(results[field.id])) {
        fieldIdsForLayout.push(fieldId);
      }
    }
  });

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={group.name}
          subtitle={subheading}
          subtitleIcon={subheadingIcon}
          onPressSubtitle={onPressSubheading}
          onPressSaveButton={onPressSaveButton}
          onPressBackButton={onPressBackButton}
          onPressCancelButton={onPressCancelButton}
          saveButtonText={saveButtonText}
          menuRight={headerMenuRight}
        />
      }
    >
      {fieldIdsForLayout.map((fieldId) => {
        const field = fields[fieldId]!;
        return (
          <Field
            key={fieldKeys[field.id] || `fk-${field.id}`}
            context={context}
            field={field}
            editable={!!editableFieldIds?.includes(fieldId)}
            required={!!requiredFieldIds?.includes(fieldId)}
            value={results[field.id]?.value}
            pin={results[field.id]?.pin || undefined}
            showRevert={!!resultsNew?.[field.id]}
            previousValue={resultsOld?.[field.id]?.value}
            previousPin={resultsOld?.[field.id]?.pin || undefined}
            onEsc={onEsc}
            onChange={onChange}
            onRevert={onRevert}
            shouldBeHidden={!!fieldIsHiddenByCriteria[fieldId]}
            instanceId={null}
            showInvalid={showInvalid}
          />
        );
      })}
    </Page>
  );
};
